import { useEffect, useRef, useState } from 'react'
import { Globe, Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { SUPPORTED_LOCALES, type SupportedLocale, tagToUserLocale } from '../i18n'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../lib/api'

interface Props {
  isLight: boolean
  variant?: 'topbar' | 'inline'
}

export default function LanguageSwitcher({ isLight, variant = 'topbar' }: Props) {
  const { i18n, t } = useTranslation('common')
  const auth = useAuth()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const current = (i18n.language || 'es').toLowerCase() as SupportedLocale

  const choose = async (locale: SupportedLocale) => {
    setOpen(false)
    if (locale === current) return
    await i18n.changeLanguage(locale)
    // Si hay sesión, persistir en el backend.
    if (auth.status === 'authenticated') {
      try {
        await api.patch('/auth/me', { locale: tagToUserLocale(locale) })
        await auth.refresh()
      } catch {
        // si falla, el cambio sigue activo en cliente; persistido en localStorage
      }
    }
  }

  if (variant === 'inline') {
    return (
      <div className="flex flex-wrap gap-2">
        {SUPPORTED_LOCALES.map((loc) => (
          <button
            key={loc}
            type="button"
            onClick={() => void choose(loc)}
            className={[
              'px-3 py-1.5 rounded-lg text-sm font-medium border transition',
              current === loc
                ? 'border-jr2-500 bg-jr2-500/15 text-jr2-700 dark:text-jr2-200'
                : isLight
                  ? 'border-slate-300 t-secondary hover:bg-slate-100'
                  : 'border-slate-700 t-secondary hover:bg-slate-800/60',
            ].join(' ')}
            aria-pressed={current === loc}
          >
            {t(`language.${loc}`)}
          </button>
        ))}
      </div>
    )
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={[
          'w-9 h-9 grid place-items-center rounded-full transition',
          isLight
            ? 'hover:bg-slate-200/60 text-slate-700'
            : 'hover:bg-slate-800/60 text-slate-300',
        ].join(' ')}
        aria-label={t('language.label')}
        title={t('language.label')}
      >
        <Globe size={18} />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-40 surface-deep p-1 shadow-xl animate-slideUp z-30"
          style={{ background: 'var(--bg-surface-strong)' }}
        >
          {SUPPORTED_LOCALES.map((loc) => (
            <button
              key={loc}
              type="button"
              onClick={() => void choose(loc)}
              className="w-full flex items-center justify-between px-3 py-2 rounded text-sm t-secondary hover:bg-hover"
            >
              <span>{t(`language.${loc}`)}</span>
              {current === loc && <Check size={14} className="text-jr2-500" />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
